import React from 'react';
import Locale from 'localization/Localization';
import moment from 'moment';
import { ContentCode, ContentCodeStartType } from 'models/Content';
import { AppServices } from 'services/AppServices';
import { Spinner } from 'components/common/Spinner';
import { ResultMessage, ResultStyle, ResultMessageView } from '../common/ResultMessageView';
import { UserProvider, UserConsumer } from 'components/contexts/UserContext';

interface ContentCodeFormProps {
	contentId: string;
	onCodeCreated: () => void;
}

interface ContentCodeFormState {
	startType: ContentCodeStartType;
	startTime: moment.Moment;
	validFrom: moment.Moment;
	validThru: moment.Moment;
	maxActivations: number;

	minStartDate: moment.Moment;
	maxStartDate: moment.Moment;

	submitInProgress: boolean;
	resultMessage?: ResultMessage;
}

export class ContentCodeForm extends React.Component<ContentCodeFormProps, ContentCodeFormState>
{
	constructor(props: ContentCodeFormProps) {
		super(props);

		const minDate = moment.utc();
		const maxDate = moment.utc().add(1, 'year');

		this.state = {
			startType: ContentCodeStartType.Activation,
			startTime: minDate,
			validFrom: minDate,
			validThru: maxDate,
			maxActivations: 1,
			minStartDate: minDate,
			maxStartDate: maxDate,
			submitInProgress: false
		};

		this.onStartTypeChanged = this.onStartTypeChanged.bind(this);
		this.onStartTimeChanged = this.onStartTimeChanged.bind(this);
		this.onMaxActivationsChanged = this.onMaxActivationsChanged.bind(this);
		this.onSubmitContentCode = this.onSubmitContentCode.bind(this);
		this.onValidFromDateChanged = this.onValidFromDateChanged.bind(this);
		this.onValidThruDateChanged = this.onValidThruDateChanged.bind(this);
	}

	private onStartTypeChanged(event: React.ChangeEvent<HTMLSelectElement>) {
		try {
			let value = event.target.value as keyof typeof ContentCodeStartType;
			var type = ContentCodeStartType[value];
		}
		catch (e) {
			console.log("Failed to parse ContentCodeStartType");
			return;
		}

		this.setState({
			startType: type
		});
	}

	private onStartTimeChanged(event: React.ChangeEvent<HTMLInputElement>) {
		let value = moment.utc(event.target.value);

		this.setState({
			startTime: value,
		});
	}

	private onMaxActivationsChanged(event: React.ChangeEvent<HTMLInputElement>) {
		let value = parseInt(event.target.value);

		if (value === undefined) {
			value = 1;
		}

		this.setState({
			maxActivations: value
		});
	}

	private onValidFromDateChanged(event: React.ChangeEvent<HTMLInputElement>) {
		let value = moment.utc(event.target.value);

		this.setState({
			validFrom: value,
			minStartDate: value,
			startTime: this.state.startTime < value ? value : this.state.startTime
		});
	}

	private onValidThruDateChanged(event: React.ChangeEvent<HTMLInputElement>) {
		let value = moment.utc(event.target.value);

		this.setState({
			validThru: value,
			maxStartDate: value,
			startTime: this.state.startTime > value ? value : this.state.startTime
		});
	}

	private async onSubmitContentCode(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();

		var code = new ContentCode();
		code.contentId = this.props.contentId;
		code.maxActivations = this.state.maxActivations;
		code.startType = this.state.startType;
		code.validFrom = this.state.validFrom.startOf('day').toISOString(true);
		code.validThru = this.state.validThru.endOf('day').toISOString(true);

		if (this.state.startType === ContentCodeStartType.Date) {
			code.startTime = this.state.startTime.startOf('day').toISOString(true);
		}

		this.setState({
			submitInProgress: true,
			resultMessage: undefined
		});

		AppServices.contentService.createContentCode(code)
			.then((status: number) => {
				if (status === 201) {
					this.setState({
						submitInProgress: false,
						resultMessage: new ResultMessage(Locale.get("contentCode_createSuccess"), ResultStyle.Success)
					});

					this.props.onCodeCreated();
				}
				else {
					let message: string = Locale.get("contentCode_createFailed");

					if (status === 409) message = Locale.get("contentCode_errorAlreadyExists");

					this.setState({
						submitInProgress: false,
						resultMessage: new ResultMessage(message, ResultStyle.Error)
					});
				}

				UserProvider.refresh();
			});
	}

	public render() {
		return <form className='program-content-configuration' onSubmit={this.onSubmitContentCode}>

			<h2>{Locale.get("contentCode_configurationTitle")}</h2>

			<p>{Locale.get("contentCode_configurationLabelType")}</p>
			<div className='content-code-form-row'>
				<select id='activation-type' value={ContentCodeStartType[this.state.startType]} onChange={this.onStartTypeChanged}>
					<option value={ContentCodeStartType[ContentCodeStartType.Activation]}>
						{Locale.get("contentCodeType_Activation")}
					</option>
					<option value={ContentCodeStartType[ContentCodeStartType.Date]}>
						{Locale.get("contentCodeType_Date")}
					</option>
				</select>

				{this.state.startType === ContentCodeStartType.Date &&
					<input id='start-date' type='date' contentEditable={true}
						value={this.state.startTime.format('YYYY-MM-DD')}
						min={this.state.minStartDate.format('YYYY-MM-DD')}
						max={this.state.maxStartDate.format('YYYY-MM-DD')}
						onChange={this.onStartTimeChanged} />
				}
			</div>

			<p>{Locale.get("contentCode_configurationLabelActivations")}</p>
			<UserConsumer>
				{
					context => <>
						{
							context.balance !== undefined &&
							<div className='content-code-form-row'>
								<div>
									<input id='activations' type='number'
										value={this.state.maxActivations} min={1}
										max={context.balance.available}
										onChange={this.onMaxActivationsChanged} />
								</div>
								<div>
									/ {context.balance.available}
								</div>
							</div>
						}
					</>
				}
			</UserConsumer>

			<div className='content-code-form-row'>
				<div>
					<p>{Locale.get("contentCode_configurationLabelValidFrom")}</p>
					<input id='valid-from' type='date' contentEditable={true}
						value={this.state.validFrom.format('YYYY-MM-DD')}
						min={moment().format('YYYY-MM-DD')}
						max={this.state.maxStartDate.format('YYYY-MM-DD')}
						onChange={this.onValidFromDateChanged} />
				</div>
				<div>
					<p>{Locale.get("contentCode_configurationLabelValidThru")}</p>
					<input id='valid-thru' type='date' contentEditable={true}
						value={this.state.validThru.format('YYYY-MM-DD')}
						min={this.state.minStartDate.format('YYYY-MM-DD')}
						onChange={this.onValidThruDateChanged} />
				</div>
			</div>

			{this.state.submitInProgress
				? <Spinner />
				: <input id='submit' type='submit' className='btn' value={Locale.get("contentCode_configurationButtonSubmit")} />
			}

			<ResultMessageView message={this.state.resultMessage} />
		</form>;
	}
}